import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { CheckCircle, CircleCheckBig, Clock } from "lucide-react";
import { useSearchParams } from "react-router-dom";

import Poll2CheckInModal from "./Poll2CheckInModal";
import { accountHasPoll2Draft, draftFromAccount } from "./poll2DraftStorage";
import {
  buildSubmitEvidence,
  emptyPoll2Draft,
  enrichPoll2Dimensions,
  poll2AllFilled,
  poll2DraftFromAccount,
  poll2FilledCount,
  type Poll2Dimension,
  type Poll2Draft,
  type Poll2Tier,
} from "./poll2Helpers";
import TeamFeedbackAccountCard from "./TeamFeedbackAccountCard";
import {
  SampleTeamFeedbackAssigned,
  SampleTeamFeedbackCompleted,
  TSF_SAMPLE_MESSAGES,
} from "./TeamFeedbackSampleState";
import { HubPageLoading } from "../../Components/EngagementEmptyPanel";
import PageHeader from "../../Components/PageHeader";
import TabsComponent from "../../Components/Tab";
import { SampleDataOverlay } from "../../Container/DiscoveryHub/DiscoveryHubSampleState";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import {
  fetchPoll2Draft,
  fetchTeamFeedback,
  savePoll2Draft,
  submitPoll2,
} from "../../Store/Reducers/SponsorEngagementSlice";
import { DEFAULT_SPONSOR_ENGAGEMENT_PROGRAM } from "../../Utils/Constants/sponsorEngagementDefaults";
import {
  MCI_MUTED,
  notificationBannerClass,
} from "../../Utils/Helper/sponsorEngagementUi";
import { toast } from "../../Utils/Helper/toast";
import CheckInPollAccordion from "../MyCheckIns/CheckInPollAccordion";
import {
  accountsForPollTab,
  pollPrimaryTab,
} from "../MyCheckIns/pollTabHelpers";

type TeamFeedbackTab = "assigned" | "completed";

const TAB_IDS: TeamFeedbackTab[] = ["assigned", "completed"];

const isTeamFeedbackTab = (value: string | null): value is TeamFeedbackTab =>
  value != null && TAB_IDS.includes(value as TeamFeedbackTab);

type TeamFeedbackPollGroup = {
  cycle_id: string;
  poll_name: string;
  period: string;
  due_date?: string | null;
  cycle_name?: string | null;
  accounts: AccountRow[];
  summary?: { submitted: number; total: number };
};

type TeamFeedbackResponse = {
  period?: string;
  due_date?: string | null;
  cycle_name?: string | null;
  poll_groups?: TeamFeedbackPollGroup[];
  accounts?: AccountRow[];
  poll2_dimensions?: Poll2Dimension[];
};

type AccountRow = {
  account_id: string;
  account_name: string;
  sponsor_id: string;
  sponsor_name?: string;
  sponsor_role?: "primary" | "secondary" | string | null;
  cycle_id?: string;
  team_feedback_received?: boolean;
  poll2_raw?: number | null;
  poll2_dimensions?: Record<string, number> | null;
  poll2_evidence?: Record<string, string> | null;
  poll2_draft?: Poll2Draft | null;
  poll2_has_draft?: boolean;
  tier?: string | null;
};

type SelectedFeedback = {
  accountId: string;
  cycleId: string;
  sponsorId: string;
};

const teamFeedbackRowKey = (cycleId: string, account: AccountRow) =>
  `${cycleId}-${account.account_id}-${account.sponsor_id}`;

const formatDueDate = (iso: string | null | undefined): string => {
  if (!iso) return "—";
  try {
    return new Date(
      iso + (iso.length === 10 ? "T12:00:00" : "")
    ).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

const isTeamFeedbackComplete = (account: AccountRow) =>
  Boolean(account.team_feedback_received);

type TeamSponsorFeedbackProps = {
  defaultTab?: TeamFeedbackTab;
};

const TeamSponsorFeedback = ({
  defaultTab = "assigned",
}: TeamSponsorFeedbackProps) => {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get("tab");

  const teamFeedback = useAppSelector(s => s.sponsorEngagement.teamFeedback);
  const submitting = useAppSelector(
    s => s.sponsorEngagement.submitPoll2.loading
  );
  const savingDraft = useAppSelector(
    s => s.sponsorEngagement.savePoll2Draft.loading
  );

  const activeTab = useMemo<TeamFeedbackTab>(
    () => (isTeamFeedbackTab(tabFromUrl) ? tabFromUrl : defaultTab),
    [tabFromUrl, defaultTab]
  );

  const data = teamFeedback.data as TeamFeedbackResponse | null;
  const loading = teamFeedback.loading;
  const [expandedPollIds, setExpandedPollIds] = useState<Set<string>>(
    () => new Set()
  );
  const [selectedFeedback, setSelectedFeedback] =
    useState<SelectedFeedback | null>(null);
  const selectedFeedbackRef = useRef<SelectedFeedback | null>(null);
  const [returnTab, setReturnTab] = useState<TeamFeedbackTab>("assigned");
  const [formDraft, setFormDraft] = useState<Poll2Draft>(emptyPoll2Draft());

  const setSelectedFeedbackSafe = (next: SelectedFeedback | null) => {
    selectedFeedbackRef.current = next;
    setSelectedFeedback(next);
  };

  const period = data?.period ?? "";
  const pollGroups: TeamFeedbackPollGroup[] = data?.poll_groups ?? [];
  const accounts = data?.accounts ?? [];
  const dimensions = useMemo(
    () => enrichPoll2Dimensions(data?.poll2_dimensions ?? []),
    [data?.poll2_dimensions]
  );
  const poll2Tiers = DEFAULT_SPONSOR_ENGAGEMENT_PROGRAM.scoring_config
    .poll2_tiers as Poll2Tier[];

  const draftIdsByCycle = useMemo(() => {
    const map = new Map<string, Set<string>>();
    for (const group of pollGroups) {
      if (!group.cycle_id) continue;
      const ids = new Set<string>();
      for (const acct of group.accounts ?? []) {
        if (accountHasPoll2Draft(acct)) {
          ids.add(teamFeedbackRowKey(group.cycle_id, acct));
        }
      }
      map.set(group.cycle_id, ids);
    }
    return map;
  }, [pollGroups]);

  const loadQueue = useCallback(async () => {
    const result = await dispatch(fetchTeamFeedback());
    if (fetchTeamFeedback.fulfilled.match(result)) {
      const payload = result.payload as TeamFeedbackResponse;
      const groups = payload.poll_groups ?? [];
      setExpandedPollIds(new Set(groups.map(g => g.cycle_id).filter(Boolean)));
      return payload;
    }
    toast.error((result.payload as string) || "Failed to load check-ins");
    return null;
  }, [dispatch]);

  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  useEffect(() => {
    if (defaultTab !== "assigned" && !searchParams.get("tab")) {
      setSearchParams({ tab: defaultTab }, { replace: true });
    }
  }, [defaultTab, searchParams, setSearchParams]);

  useEffect(() => {
    if (tabFromUrl === "draft") {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.delete("tab");
        return next;
      });
    }
  }, [tabFromUrl, setSearchParams]);

  const handleTabChange = useCallback(
    (tabId: string) => {
      if (!isTeamFeedbackTab(tabId)) return;
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        if (tabId === "assigned") {
          next.delete("tab");
        } else {
          next.set("tab", tabId);
        }
        return next;
      });
    },
    [setSearchParams]
  );

  const findAccount = useCallback(
    (accountId: string, cycleId: string, sponsorId: string) => {
      const group = pollGroups.find(g => g.cycle_id === cycleId);
      return (group?.accounts ?? []).find(
        a => a.account_id === accountId && a.sponsor_id === sponsorId
      );
    },
    [pollGroups]
  );

  const getDraft = useCallback((account: AccountRow): Poll2Draft => {
    if (account.team_feedback_received && account.poll2_dimensions) {
      return poll2DraftFromAccount(account);
    }
    return draftFromAccount(account) ?? emptyPoll2Draft();
  }, []);

  const makeIsDraft = useCallback(
    (_cycleId: string, _draftIds: Set<string>) => (account: AccountRow) =>
      accountHasPoll2Draft(account),
    []
  );

  const tabPartitions = useMemo(() => {
    let assignedPollCount = 0;
    let completedPollCount = 0;
    for (const group of pollGroups) {
      const cycleId = group.cycle_id;
      const draftIds = draftIdsByCycle.get(cycleId) ?? new Set<string>();
      const accounts = group.accounts ?? [];
      const isDraft = makeIsDraft(cycleId, draftIds);
      const tab = pollPrimaryTab(accounts, isDraft, isTeamFeedbackComplete);
      if (!tab) continue;
      if (tab === "assigned") {
        assignedPollCount += 1;
      } else {
        completedPollCount += 1;
      }
    }
    return { assignedPollCount, completedPollCount };
  }, [pollGroups, draftIdsByCycle, makeIsDraft]);

  const { assignedPollCount, completedPollCount } = tabPartitions;

  const completionDone = accounts.filter(a => a.team_feedback_received).length;
  const completionTotal = accounts.length;

  const openFeedbackForm = async (
    accountId: string,
    cycleId: string,
    sponsorId: string,
    fromTab: TeamFeedbackTab
  ) => {
    setReturnTab(fromTab);
    const acct = findAccount(accountId, cycleId, sponsorId);
    // Reset form before opening so a prior submission/draft never flashes.
    let nextDraft = emptyPoll2Draft();
    if (acct?.team_feedback_received && acct.poll2_dimensions) {
      nextDraft = poll2DraftFromAccount(acct);
    } else {
      const embeddedDraft = draftFromAccount(acct);
      if (embeddedDraft) nextDraft = embeddedDraft;
    }
    setFormDraft(nextDraft);
    setSelectedFeedbackSafe({ accountId, cycleId, sponsorId });

    if (!acct || acct.team_feedback_received || draftFromAccount(acct)) {
      return;
    }

    const draftResult = await dispatch(
      fetchPoll2Draft({ accountId, cycleId, sponsorId })
    );
    const current = selectedFeedbackRef.current;
    if (
      !current ||
      current.accountId !== accountId ||
      current.cycleId !== cycleId ||
      current.sponsorId !== sponsorId
    ) {
      return;
    }
    if (fetchPoll2Draft.fulfilled.match(draftResult) && draftResult.payload) {
      setFormDraft(draftResult.payload as Poll2Draft);
    } else {
      setFormDraft(emptyPoll2Draft());
    }
  };

  const closeFeedbackForm = () => {
    setSelectedFeedbackSafe(null);
    setFormDraft(emptyPoll2Draft());
    handleTabChange(returnTab);
  };

  const handleSaveDraft = async () => {
    if (!selectedFeedback || savingDraft) return;
    const { accountId, cycleId, sponsorId } = selectedFeedback;
    if (poll2FilledCount(formDraft) === 0) {
      toast.error("Rate at least one dimension before saving a draft");
      return;
    }
    const result = await dispatch(
      savePoll2Draft({
        accountId,
        cycleId,
        sponsorId,
        draft: formDraft,
      })
    );
    if (savePoll2Draft.fulfilled.match(result)) {
      toast.success("Draft saved successfully");
      setSelectedFeedbackSafe(null);
      setFormDraft(emptyPoll2Draft());
      await loadQueue();
    } else {
      toast.error((result.payload as string) || "Failed to save draft");
    }
  };

  const handleSubmit = async () => {
    if (!selectedFeedback) return;
    const { accountId, cycleId, sponsorId } = selectedFeedback;
    const account = findAccount(accountId, cycleId, sponsorId);
    if (!account || !poll2AllFilled(formDraft)) return;

    const result = await dispatch(
      submitPoll2({
        accountId,
        cycleId,
        sponsorId,
        payload: {
          involvement: formDraft.involvement,
          relationship: formDraft.relationship,
          knowledge: formDraft.knowledge,
          evidence: buildSubmitEvidence(
            formDraft,
            dimensions.map(d => d.id)
          ),
          comments: formDraft.comments.trim() || undefined,
        },
      })
    );
    if (submitPoll2.fulfilled.match(result)) {
      toast.success("Feedback submitted");
      setSelectedFeedbackSafe(null);
      setFormDraft(emptyPoll2Draft());
      await loadQueue();
      const group = pollGroups.find(g => g.cycle_id === cycleId);
      const allDone = (group?.accounts ?? []).every(
        a =>
          isTeamFeedbackComplete(a) ||
          (a.account_id === accountId && a.sponsor_id === sponsorId)
      );
      handleTabChange(allDone ? "completed" : "assigned");
    } else {
      toast.error((result.payload as string) || "Submit failed");
    }
  };

  const tabs = useMemo(
    () => [
      {
        id: "assigned",
        label: (
          <span className="inline-flex items-center gap-2.5">
            <Clock className="h-[18px] w-[18px]" size={18} />
            Assigned Polls
          </span>
        ),
        count: assignedPollCount,
      },
      {
        id: "completed",
        label: (
          <span className="inline-flex items-center gap-2.5">
            <CheckCircle className="h-[18px] w-[18px]" size={18} />
            Completed Polls
          </span>
        ),
        count: completedPollCount,
      },
    ],
    [assignedPollCount, completedPollCount]
  );

  const togglePollExpanded = (cycleId: string) => {
    setExpandedPollIds(prev => {
      const next = new Set(prev);
      if (next.has(cycleId)) {
        next.delete(cycleId);
      } else {
        next.add(cycleId);
      }
      return next;
    });
  };

  const rowsForTab = (tab: TeamFeedbackTab) => (g: TeamFeedbackPollGroup) => {
    const draftIds = draftIdsByCycle.get(g.cycle_id) ?? new Set<string>();
    const accounts = g.accounts ?? [];
    const isDraft = makeIsDraft(g.cycle_id, draftIds);
    if (pollPrimaryTab(accounts, isDraft, isTeamFeedbackComplete) !== tab) {
      return [];
    }
    const rows = accountsForPollTab(
      accounts,
      tab,
      isDraft,
      isTeamFeedbackComplete
    );
    return [...rows].sort((a, b) => {
      const byAccount = String(a.account_name || "").localeCompare(
        String(b.account_name || ""),
        undefined,
        { sensitivity: "base" }
      );
      if (byAccount !== 0) return byAccount;
      return String(a.sponsor_name || "").localeCompare(
        String(b.sponsor_name || ""),
        undefined,
        { sensitivity: "base" }
      );
    });
  };

  const renderAccountGrid = (
    rows: AccountRow[],
    actionTab: TeamFeedbackTab,
    cycleId: string,
    draftIds: Set<string>
  ) => {
    const isDraftFn = makeIsDraft(cycleId, draftIds);
    const badgeMode =
      actionTab === "completed" ? "completed-only" : "draft-only";
    return (
      <div className="grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-3.5">
        {rows.map(row => (
          <TeamFeedbackAccountCard
            key={teamFeedbackRowKey(cycleId, row)}
            account={row}
            isDraft={isDraftFn(row)}
            draft={getDraft(row)}
            poll2Tiers={poll2Tiers}
            showStatusBadge={badgeMode}
            onAction={() =>
              openFeedbackForm(
                row.account_id,
                cycleId,
                row.sponsor_id,
                actionTab
              )
            }
          />
        ))}
      </div>
    );
  };

  const renderPollGroupsForTab = (
    tab: TeamFeedbackTab,
    emptyHint: string,
    actionTab: TeamFeedbackTab
  ) => {
    const pickRows = rowsForTab(tab);
    const visibleGroups = pollGroups.filter(g => {
      const draftIds = draftIdsByCycle.get(g.cycle_id) ?? new Set<string>();
      const isDraft = makeIsDraft(g.cycle_id, draftIds);
      return (
        pollPrimaryTab(g.accounts ?? [], isDraft, isTeamFeedbackComplete) ===
        tab
      );
    });

    if (visibleGroups.length === 0) {
      return (
        <div className="border border-dashed border-gray-300 rounded-2xl py-16 px-8 text-center bg-white">
          <p className={`text-sm ${MCI_MUTED}`}>{emptyHint}</p>
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-5 mt-1">
        {visibleGroups.map(group => {
          const cycleId = group.cycle_id;
          const rows = pickRows(group);
          const draftIds = draftIdsByCycle.get(cycleId) ?? new Set<string>();
          const pollName =
            group.poll_name?.trim() ||
            group.cycle_name?.trim() ||
            (group.period ? `${group.period} Check-In` : "Check-In");
          const groupDone = group.summary?.submitted ?? 0;
          const groupTotal = group.summary?.total ?? rows.length;

          return (
            <CheckInPollAccordion
              key={cycleId}
              pollName={pollName}
              period={group.period || period}
              cycle={{
                id: cycleId,
                name: group.cycle_name ?? group.poll_name,
                period: group.period,
                due_date: group.due_date ?? undefined,
              }}
              accountCount={(group.accounts ?? []).length}
              poll1Done={groupDone}
              poll1Total={groupTotal}
              progressLabel="Submitted"
              isExpanded={expandedPollIds.has(cycleId)}
              onToggleExpand={() => togglePollExpanded(cycleId)}
              emptyHint={emptyHint}
            >
              {renderAccountGrid(rows, actionTab, cycleId, draftIds)}
            </CheckInPollAccordion>
          );
        })}
      </div>
    );
  };

  const dueDate = data?.due_date ?? pollGroups[0]?.due_date;

  const nudgeBanner =
    completionTotal > 0 && completionDone < completionTotal ? (
      <div className={`${notificationBannerClass("amber")} justify-between`}>
        <div className="flex items-start gap-2.5 flex-1 min-w-0">
          <Clock className="shrink-0 mt-px" size={18} />
          <div className="min-w-0">
            <div className="font-bold text-[13px] mb-px">
              Sponsor Feedback open — {completionDone} of {completionTotal}{" "}
              accounts completed
            </div>
            <div className="text-xs opacity-90">
              {dueDate ? `Due ${formatDueDate(dueDate)}. ` : ""}
              Rate the exec sponsor for each covered account (3 dimensions, 1–5
              scale).
            </div>
          </div>
        </div>
        {period ? (
          <span className="shrink-0 font-bold text-[13px] whitespace-nowrap ml-3">
            {period}
          </span>
        ) : null}
      </div>
    ) : completionTotal > 0 ? (
      <div className={`${notificationBannerClass("green")} justify-between`}>
        <div className="flex items-center gap-2.5 flex-1 min-w-0">
          <CheckCircle className="shrink-0" size={18} />
          <div className="min-w-0">
            <div className="font-bold text-[13px] mb-px">
              Sponsor Feedback complete
            </div>
            <div className="text-xs opacity-90">
              All {completionTotal} of your accounts submitted. Thank you.
            </div>
          </div>
        </div>
        {period ? (
          <span className="shrink-0 font-bold text-[13px] whitespace-nowrap ml-3">
            {period}
          </span>
        ) : null}
      </div>
    ) : null;

  const hasActivePolls = pollGroups.length > 0;
  const showSamplePreview = !loading && pollGroups.length === 0;

  const modalAccount = selectedFeedback
    ? findAccount(
        selectedFeedback.accountId,
        selectedFeedback.cycleId,
        selectedFeedback.sponsorId
      )
    : null;

  const modalGroup = selectedFeedback
    ? pollGroups.find(g => g.cycle_id === selectedFeedback.cycleId)
    : null;

  const modalCycleName =
    modalGroup?.poll_name?.trim() ||
    modalGroup?.cycle_name?.trim() ||
    undefined;

  return (
    <div className="flex min-h-full flex-1 flex-col bg-white overflow-hidden">
      <PageHeader
        title="My Check-Ins"
        subTitle="Bi-quarterly Poll 2 — rate the exec sponsor for each covered account"
        icon={CircleCheckBig}
      />
      <div className="w-full flex-1 bg-white p-3 md:p-6">
        <div className="flex flex-col gap-6">
          <TabsComponent
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={handleTabChange}
            variant="tab-segmented"
          />
          <div className="px-1 pb-6 flex flex-col gap-5">
            {loading ? (
              <HubPageLoading message="Loading your check-ins…" />
            ) : showSamplePreview ? (
              <SampleDataOverlay message={TSF_SAMPLE_MESSAGES[activeTab]}>
                {activeTab === "assigned" ? (
                  <SampleTeamFeedbackAssigned poll2Tiers={poll2Tiers} />
                ) : (
                  <SampleTeamFeedbackCompleted poll2Tiers={poll2Tiers} />
                )}
              </SampleDataOverlay>
            ) : !hasActivePolls ? (
              <div className="border border-dashed border-gray-300 rounded-2xl py-16 px-8 text-center bg-white">
                <h3 className="text-base font-semibold text-gray-900 mb-1">
                  No active check-in cycle
                </h3>
                <p className={`text-sm ${MCI_MUTED}`}>
                  Your program lead will launch the quarterly poll from ExecOps
                  Discovery Hub.
                </p>
              </div>
            ) : (
              <>
                {nudgeBanner}
                {activeTab === "assigned" &&
                  renderPollGroupsForTab(
                    "assigned",
                    "All accounts are completed for these polls.",
                    "assigned"
                  )}
                {activeTab === "completed" &&
                  renderPollGroupsForTab(
                    "completed",
                    "Polls appear here after feedback is submitted for every assigned account.",
                    "completed"
                  )}
              </>
            )}
          </div>
        </div>
      </div>
      <Poll2CheckInModal
        isOpen={Boolean(selectedFeedback && modalAccount)}
        onClose={closeFeedbackForm}
        accountId={modalAccount?.account_id ?? ""}
        accountName={modalAccount?.account_name ?? ""}
        sponsorName={modalAccount?.sponsor_name}
        sponsorRole={modalAccount?.sponsor_role}
        cycleName={modalCycleName}
        period={period}
        dimensions={dimensions}
        poll2Tiers={poll2Tiers}
        draft={formDraft}
        submitted={Boolean(modalAccount?.team_feedback_received)}
        submitting={submitting}
        savingDraft={savingDraft}
        onDraftChange={setFormDraft}
        onSaveDraft={handleSaveDraft}
        onSubmit={handleSubmit}
      />
    </div>
  );
};

export default TeamSponsorFeedback;
